<script lang="ts">
export default {
  name: "BaseDropdownCheckbox",
};
</script>

<script setup lang="ts">
import { computed } from "vue";
import { FilterDropdownCheckboxProps } from "@/types/filter.types";
import { ensureArray } from "@/utils/gallery/ensureArray";
import { BaseButtonKindEnum } from "@/components/Base/Button";

const props = withDefaults(defineProps<FilterDropdownCheckboxProps>(), {
  kind: BaseButtonKindEnum.NAKED,
  selectedValues: (value) =>
    ensureArray(
      value as unknown as string | string[]
    ) as FilterDropdownCheckboxProps["selectedValues"],
});

const emit = defineEmits(["update:selectedValues", "change"]);

const selectedOptions = computed({
  get: () => ensureArray(props.selectedValues),
  set: (newValues) => {
    emit("update:selectedValues", newValues);
    emit("change", newValues);
  },
});

const selectedCount = computed(() => selectedOptions.value.length);

const toggleOption = (optionValue: string) => {
  selectedOptions.value = selectedOptions.value.includes(optionValue)
    ? selectedOptions.value.filter((value) => value !== optionValue)
    : [...selectedOptions.value, optionValue];
};

const isSelected = (optionValue: string) =>
  selectedOptions.value.includes(optionValue);

const isOpen = ref(false);
</script>

<template>
  <BaseDropdown
    :id="id"
    auto-close="outside"
    dropdown-menu-class="p-3"
    button-aria-label="Filter"
    :kind="kind"
    @show="isOpen = true"
    @hide="isOpen = false"
  >
    <template #button>
      <div class="d-flex align-items-center">
        <span class="font-sm">{{ label }}</span>
        <BaseNotificationBadge
          :count="selectedCount"
          size="lg"
          position="absolute"
          top="-5px"
          right="-5px"
        />
        <i
          class="bi ms-2"
          :class="isOpen ? 'bi-chevron-up' : 'bi-chevron-down'"
        ></i>
      </div>
    </template>
    <div class="dropdown-menu-content">
      <div v-for="option in options" :key="option.value" class="checkbox-item">
        <div class="form-check">
          <input
            :id="`${id}_${option.value}`"
            class="form-check-input"
            type="checkbox"
            :value="option.value"
            :checked="isSelected(option.value.toString())"
            @change="toggleOption(option.value.toString())"
          />
          <label :for="`${id}_${option.value}`" class="form-check-label">
            {{ option.label }}
          </label>
        </div>
      </div>
    </div>
  </BaseDropdown>
</template>

<style lang="scss" scoped>
.dropdown-menu-content {
  display: grid;
  grid-template-columns: repeat(2, auto);
  gap: 0.5rem;
  min-width: 200px;
  max-width: max-content;
}

.checkbox-item {
  white-space: nowrap;
}

.form-check-label {
  padding-left: 0.25rem;
}
</style>
